/**
 * Klick-Interaktionen für gestreamtes Kapitel-HTML — die App-Entsprechung zur
 * globalen Delegation der Web-App (app.js: aktiviereAskKi / blaettereSteps).
 *
 * Beide Frontends rendern denselben Generator-HTML-String; die Buttons tragen
 * data-Attribute. Das Web verdrahtet sie global; die App hatte dafür nichts —
 * deshalb waren „Frag die Kurs-KI", Prompt-Kopieren, Anbieter-Karten-Fragen und
 * das Schritt-Karussell in der App TOT. Diese reinen Funktionen schließen das.
 */
import { handleWerkstattClick } from "./werkstatt-personal";

export interface LessonClickDeps {
  /** Frage direkt an den Coach senden (Sheet öffnen + fragen). */
  askQuestion: (text: string) => void;
  /** Frage mit [Platzhaltern] ins Coach-Eingabefeld einsetzen (nicht senden). */
  insertIntoCoach: (text: string) => void;
  /** Prompt mit [Platzhaltern]: Eingabemaske öffnen (Frage je Platzhalter, Text/Sprache). */
  openPromptDialog: (text: string) => void;
  /** Kurzer Bestätigungs-Toast. */
  toast: (msg: string) => void;
}

/**
 * Schritt-Karussell (Generator :::schritte blättern): eine Karte sichtbar, ◄ ►
 * tauscht die .aktiv-Karte und zieht Indikator + disabled-Zustände nach.
 * Kein Umlauf am Rand (1:1 zur Web-App blaettereSteps).
 */
export function blaettereSteps(root: Element | null, dir: number): void {
  if (!root) return;
  const cards = Array.from(root.querySelectorAll<HTMLElement>(".step-card"));
  if (!cards.length) return;
  let i = cards.findIndex((c) => c.classList.contains("aktiv"));
  if (i < 0) i = 0;
  const ziel = Math.max(0, Math.min(cards.length - 1, i + dir));
  if (ziel === i) return;
  cards[i].classList.remove("aktiv");
  cards[ziel].classList.add("aktiv");
  const ind = root.querySelector<HTMLElement>("[data-step-indicator]");
  const einheit = (root as HTMLElement).dataset.stepEinheit || "Schritt";
  if (ind) ind.textContent = `${einheit} ${ziel + 1} von ${cards.length}`;
  const prev = root.querySelector<HTMLButtonElement>("[data-step-prev]");
  const next = root.querySelector<HTMLButtonElement>("[data-step-next]");
  if (prev) prev.disabled = ziel === 0;
  if (next) next.disabled = ziel === cards.length - 1;
}

async function copyText(text: string, label: string, toast: (m: string) => void): Promise<void> {
  if (!text) return;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      toast(label);
      return;
    }
  } catch {
    /* Fallback unten */
  }
  // Fallback für ältere WebViews ohne Clipboard-API.
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    toast(ok ? label : "Kopieren nicht möglich");
  } catch {
    toast("Kopieren nicht möglich");
  }
}

/**
 * Übungsmaterial als Datei: auf Mobilgeräten bevorzugt der Teilen-Dialog
 * (in der iOS-PWA gibt es keinen klassischen Download-Ordner), sonst Blob-Download.
 */
async function saveMaterial(text: string, name: string, toast: (m: string) => void): Promise<void> {
  const filename = `${name || "uebungsmaterial"}.txt`;
  try {
    const file = new File([text], filename, { type: "text/plain" });
    const nav = navigator as Navigator & { canShare?: (d: unknown) => boolean };
    if (nav.canShare?.({ files: [file] }) && navigator.share) {
      await navigator.share({ files: [file], title: filename });
      return;
    }
  } catch {
    /* Nutzer hat Teilen abgebrochen oder nicht unterstützt → Blob-Fallback */
  }
  try {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    toast("Übungsmaterial gespeichert ✓");
  } catch {
    toast("Speichern nicht möglich");
  }
}

/**
 * Ein delegierter Klick-Handler für das ganze Lesson-<article>. Gibt true zurück,
 * wenn der Klick verarbeitet wurde (für preventDefault im Aufrufer).
 */
export function handleLessonClick(ev: MouseEvent, deps: LessonClickDeps): boolean {
  const t = ev.target as Element | null;
  if (!t || !t.closest) return false;

  // 0. Werkstatt-Personalisierung (W1): Intake/Anpassen/Reset/Ändern, Schritt-
  //    Eingabe, „Original anzeigen"-Toggle, Mikrofon. Vor den generischen
  //    Aktionen (diese Knöpfe tragen teils kein data-copy, sollen aber sicher
  //    zuerst greifen). 1:1 zur globalen Delegation der Web-App.
  if (handleWerkstattClick(ev)) return true;

  // 1. Schritt-Karussell ◄ ►
  const stepBtn = t.closest("[data-step-prev],[data-step-next]");
  if (stepBtn) {
    ev.preventDefault();
    blaettereSteps(stepBtn.closest(".step-carousel"), stepBtn.hasAttribute("data-step-next") ? 1 : -1);
    return true;
  }

  // 2. „Frag die Kurs-KI" — mit [Platzhaltern] ins Eingabefeld, sonst direkt senden.
  const ask = t.closest<HTMLElement>("[data-ask-ki]");
  if (ask) {
    ev.preventDefault();
    const text = ask.getAttribute("data-ask-ki") || "";
    if (!text) return true;
    if (ask.dataset.hasVar === "1") deps.openPromptDialog(text);
    else deps.askQuestion(text);
    return true;
  }

  // 3. Frage kopieren (eigenes Attribut, kein Konflikt mit data-copy)
  const askCopy = t.closest<HTMLElement>("[data-ask-copy]");
  if (askCopy) {
    ev.preventDefault();
    void copyText(askCopy.getAttribute("data-ask-copy") || "", "Frage kopiert ✓", deps.toast);
    return true;
  }

  // 4. Material als Datei (vor data-copy prüfen, da wk-material-copy auch data-copy trägt)
  const mdl = t.closest<HTMLElement>("[data-material-download]");
  if (mdl) {
    ev.preventDefault();
    void saveMaterial(
      mdl.getAttribute("data-material-download") || "",
      mdl.getAttribute("data-material-name") || "uebungsmaterial",
      deps.toast,
    );
    return true;
  }

  // 5. Kopieren (Prompt-Kasten, Werkstatt-Material-Kopie u. a.)
  const copy = t.closest<HTMLElement>("[data-copy]");
  if (copy) {
    ev.preventDefault();
    void copyText(copy.getAttribute("data-copy") || "", copy.dataset.copyLabel || "Kopiert ✓", deps.toast);
    return true;
  }

  return false;
}
